import m from 'mithril';
import { Store } from '../store';
import { ApiService } from '../services/api';
import { Button } from '../components/Buttons';
import { ProductCard } from '../components/ProductCard';

export const ProductDetailPage: m.Component = {
    oninit(vnode: any) {
        vnode.state.product = null;
        vnode.state.loading = true;
        vnode.state.selectedImageIndex = 0;
        vnode.state.isZooming = false;
        vnode.state.zoomX = "50%";
        vnode.state.zoomY = "50%";
        vnode.state.quantity = 1;
        
        // Reviews State
        vnode.state.reviews = [];
        vnode.state.reviewMeta = { average: 0, count: 0 };
        vnode.state.newRating = 5;
        vnode.state.newComment = '';
        vnode.state.isSubmittingReview = false;
        
        // Tabs State
        vnode.state.activeTab = 'description';
        
        const id = m.route.param('id');
        
        // Track Recently Viewed
        if (id) {
            let recentlyViewed = [];
            try {
                recentlyViewed = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
            } catch (e) {}
            // Remove if exists to push to front
            recentlyViewed = recentlyViewed.filter((pid: string) => pid !== String(id));
            recentlyViewed.unshift(String(id));
            // Keep only last 5
            recentlyViewed = recentlyViewed.slice(0, 5);
            localStorage.setItem('recently_viewed', JSON.stringify(recentlyViewed));
        }

        vnode.state.relatedProducts = [];
        const findRelated = (cat: string) => {
            vnode.state.relatedProducts = Store.state.products
                .filter(p => p.category === cat && String(p.id) !== String(id))
                .slice(0, 4);
        };

        
        // Try getting from Store first
        const storeProduct = Store.state.products.find(p => String(p.id) === id);
        if (storeProduct) {
            vnode.state.product = storeProduct;
            vnode.state.loading = false;
            document.title = `KHALID | ${storeProduct.name}`;
            findRelated(storeProduct.category);
        } else {
            // Fallback to fetch
            ApiService.getProductById(id).then(res => {
                if (res.status === 'success' && res.data.length > 0) {
                    vnode.state.product = res.data[0];
                    document.title = `KHALID | ${res.data[0].name}`;
                    findRelated(res.data[0].category);
                } else if (res.success && res.data && !Array.isArray(res.data)) {
                     // API returns object directly now based on getProductById implementation
                    vnode.state.product = res.data;
                    document.title = `KHALID | ${res.data.name}`;
                    findRelated(res.data.category);
                }
                vnode.state.loading = false;
                m.redraw();
            }).catch(() => {
                vnode.state.loading = false;
                m.redraw();
            });
        }
        
        // Fetch Reviews
        ApiService.getReviews(id).then(res => {
            if (res.success) {
                vnode.state.reviews = res.data;
                vnode.state.reviewMeta = res.meta;
                m.redraw();
            }
        });
    },
    view(vnode: any) {
        const { product, loading, selectedImageIndex = 0, isZooming = false, zoomX = "50%", zoomY = "50%", reviews, reviewMeta, newRating, newComment, isSubmittingReview, activeTab, relatedProducts = [] } = vnode.state;

        if (loading) return m('div', { class: 'container mx-auto px-6 py-8 min-h-[100dvh]' }, [
            m('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-12' }, [
                m('div', { class: 'rounded-lg bg-[var(--color-border)] aspect-[4/5] animate-pulse opacity-50' }),
                m('div', { class: 'flex flex-col justify-center gap-4' }, [
                    m('div', { class: 'h-4 w-1/4 bg-[var(--color-border)] rounded animate-pulse opacity-50' }),
                    m('div', { class: 'h-12 w-3/4 bg-[var(--color-border)] rounded animate-pulse opacity-50' }),
                    m('div', { class: 'h-8 w-1/3 bg-[var(--color-border)] rounded animate-pulse opacity-50 my-2' }),
                    m('div', { class: 'h-4 w-full bg-[var(--color-border)] rounded animate-pulse opacity-50' }),
                    m('div', { class: 'h-4 w-5/6 bg-[var(--color-border)] rounded animate-pulse opacity-50' }),
                    m('div', { class: 'h-4 w-4/6 bg-[var(--color-border)] rounded animate-pulse opacity-50' }),
                    m('div', { class: 'flex gap-4 mt-6' }, [
                        m('div', { class: 'h-12 w-32 bg-[var(--color-border)] rounded-lg animate-pulse opacity-50' }),
                        m('div', { class: 'h-12 flex-1 bg-[var(--color-border)] rounded-lg animate-pulse opacity-50' }),
                    ])
                ])
            ])
        ]);
        if (!product) return m('div', { class: 'container py-12 flex justify-center text-muted' }, 'Product not found.');

        const handleMouseMove = (e: MouseEvent) => {
            const target = e.currentTarget as HTMLElement;
            const rect = target.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            vnode.state.zoomX = `${x}%`;
            vnode.state.zoomY = `${y}%`;
        };

        return m('div', { class: 'container mx-auto px-6 animate-fade-in-up py-8 transition-colors duration-500 text-[var(--color-text-base)] bg-[var(--color-bg-base)] min-h-[100dvh]' }, [
            m('div', { class: 'grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12' }, [
                // Product Image Container and Thumbnails (5 columns)
                m('div', { class: 'lg:col-span-5 flex flex-col gap-3' }, [
                    m('div', { 
                        class: 'product-image-wrap rounded-[var(--radius-xl)] overflow-hidden shadow-sm border border-[var(--color-border)] bg-[var(--color-surface)] aspect-square max-h-[55vh] relative flex items-center justify-center'
                    }, [
                        m('img', { 
                            src: (product.images && product.images.length > 0) ? (product.images[selectedImageIndex] || product.images[0]) : `https://picsum.photos/seed/${product.id}/1000/1000`,
                            loading: 'lazy',
                            class: 'w-full h-full object-contain mix-blend-multiply opacity-95 transition-transform duration-500 hover:scale-[1.03]'
                        })
                    ]),
                    // Thumbnails (Compact Row)
                    (product.images && product.images.length > 1) ? m('div', { class: 'flex gap-2 sm:gap-3 overflow-x-auto pb-2 snap-x' }, 
                        product.images.map((imgUrl: string, idx: number) => {
                            return m('button', {
                                onclick: () => { vnode.state.selectedImageIndex = idx; m.redraw(); },
                                class: `w-20 h-20 sm:w-24 sm:h-24 snap-start rounded-[var(--radius-lg)] overflow-hidden border-2 transition-all duration-300 hover-spring bg-[var(--color-surface)] flex-shrink-0 cursor-pointer p-0 m-0
                                        ${selectedImageIndex === idx ? 'border-[var(--color-primary)] shadow-[0_4px_15px_-3px_var(--color-primary-glow)] transform scale-[1.02] opacity-100' : 'border-[var(--color-border)] opacity-60 hover:opacity-100 hover:border-[var(--color-primary)]/50'}`
                            }, [
                                m('img', { src: imgUrl, class: 'w-full h-full object-contain mix-blend-multiply p-1' })
                            ])
                        })
                    ) : null
                ]),
                // Product Metadata Container (7 columns)
                m('div', { class: 'lg:col-span-7 flex flex-col justify-start pt-2 lg:pt-6' }, [
                    m('span', { class: 'text-sm text-[var(--color-secondary)] font-bold uppercase tracking-widest mb-2' }, 'Product Details'),
                    m('h1', { class: 'text-4xl lg:text-5xl font-extrabold mb-3 tracking-tight' }, product.name),
                    
                    // Rating Overview Summary
                    m('div', { class: 'flex items-center gap-3 mb-5' }, [
                        m('div', { class: 'flex items-center text-[var(--color-primary)] opacity-90' }, 
                            Array.from({ length: 5 }).map((_, i) => 
                                m('iconify-icon', { icon: i < Math.round(reviewMeta?.average || 0) ? 'lucide:star' : 'lucide:star' })
                            )
                        ),
                        m('a', { href: '#reviews', class: 'text-sm font-bold opacity-60 hover:opacity-100 hover:text-[var(--color-primary)] transition-colors' }, 
                            `${reviewMeta?.average || 0} (${reviewMeta?.count || 0} reviews)`
                        ),
                    ]),
                    // Price and Stock Indicator
                    m('div', { class: 'flex items-center gap-4 mb-6' }, [
                        m('span', { class: 'text-3xl font-black text-[var(--color-primary)]' }, `$${Number(product.price).toFixed(2)}`),
                        m('span', { class: `text-sm font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 ${product.stock > 0 ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}` }, [
                            m('iconify-icon', { icon: product.stock > 0 ? 'lucide:check-circle-2' : 'lucide:x-circle', class: 'text-base' }),
                            product.stock > 0 ? `${product.stock} left in stock` : 'Out of stock'
                        ])
                    ]),
                    m('div', { class: 'flex items-stretch gap-3 sm:gap-4 mb-8 h-14' }, [
                        m('div', { class: 'flex items-center border border-[var(--color-border)] rounded-lg overflow-hidden bg-[var(--color-surface)] shadow-inner shrink-0' }, [
                            m(Button, { variant: 'ghost', class: 'text-[var(--color-primary)] !w-12 !h-full !rounded-none !p-0 flex items-center justify-center', onclick: () => { if (vnode.state.quantity > 1) vnode.state.quantity--; m.redraw(); } }, '-'),
                            m('span', { class: 'w-10 text-center font-semibold text-lg flex items-center justify-center h-full' }, vnode.state.quantity),
                            m(Button, { variant: 'ghost', class: 'text-[var(--color-primary)] !w-12 !h-full !rounded-none !p-0 flex items-center justify-center', onclick: () => { vnode.state.quantity++; m.redraw(); } }, '+'),
                        ]),
                        m(Button, { 
                            variant: 'primary',
                            icon: 'lucide:shopping-cart',
                            class: 'flex-1 !h-full !text-base',
                            onclick: () => {
                                const existing = Store.state.cart.find((c: any) => (c.product?.id || c.id) === product.id);
                                if (existing) {
                                    existing.quantity += vnode.state.quantity;
                                } else {
                                    Store.state.cart.push({
                                        id: Date.now(), 
                                        quantity: vnode.state.quantity, 
                                        product: { id: product.id, name: product.name, price: String(product.price), image: (product.images && product.images.length > 0) ? product.images[0] : null }
                                    });
                                }
                                Store.addToast('Added to cart!', 'success');
                            }
                        }, 'Add to Cart'),
                        m('button', {
                            class: `flex items-center justify-center w-14 h-full rounded-lg border transition-all hover-spring shrink-0 ${
                                Store.state.favorites.some(f => f.id === product.id)
                                ? 'bg-red-50 border-red-500 text-red-500'
                                : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-base)] hover:border-red-500 hover:text-red-500'
                            }`,
                            onclick: () => Store.toggleFavorite(product)
                        }, m('iconify-icon', { 
                            icon: 'lucide:heart', 
                            class: `text-xl ${Store.state.favorites.some(f => f.id === product.id) ? 'animate-scale-in drop-shadow-md text-red-500' : ''}`
                        }))
                    ]),
                    m('div', { class: 'border-t border-[var(--color-border)] pt-8 mt-4' }, [
                        m('div', { class: 'flex items-center gap-3 text-sm opacity-80 mb-4 font-medium' }, [
                            m('iconify-icon', { icon: 'lucide:truck', class: 'text-xl text-[var(--color-secondary)]' }),
                            m('span', 'Free Worldwide Shipping via Express')
                        ]),
                        m('div', { class: 'flex items-center gap-3 text-sm opacity-80 font-medium' }, [
                            m('iconify-icon', { icon: 'lucide:shield-check', class: 'text-xl text-[var(--color-secondary)]' }),
                            m('span', '2 Year Global Warranty Included')
                        ])
                    ])
                ])
            ]),

            /* ── TABBED INFO & REVIEWS ────────────────────────────────────────── */
            m('div', { class: 'mt-16 pt-8 border-t border-[var(--color-border)]' }, [
                // Tab Navigation
                m('div', { class: 'flex items-center gap-6 md:gap-10 border-b border-[var(--color-border)] mb-10 overflow-x-auto no-scrollbar' }, [
                    ['description', 'specifications', 'reviews'].map(tab => 
                        m('button', {
                            class: `pb-4 text-base md:text-lg font-bold capitalize transition-colors whitespace-nowrap border-b-2 relative ${activeTab === tab ? 'text-[var(--color-primary)] border-[var(--color-primary)]' : 'text-[var(--color-text-base)] opacity-50 border-transparent hover:opacity-100'}`,
                            onclick: () => { vnode.state.activeTab = tab; m.redraw(); }
                        }, tab === 'reviews' ? `Reviews (${reviewMeta?.count || 0})` : tab)
                    )
                ]),

                // Tab Content: Description
                activeTab === 'description' && m('div', { class: 'animate-fade-in-up max-w-4xl' }, [
                    m('h3', { class: 'text-2xl font-black mb-6' }, 'About this Product'),
                    m('p', { class: 'text-lg opacity-80 leading-relaxed whitespace-pre-wrap' }, product.description || 'No description available.')
                ]),

                // Tab Content: Specifications
                activeTab === 'specifications' && m('div', { class: 'animate-fade-in-up max-w-4xl' }, [
                    m('h3', { class: 'text-2xl font-black mb-6' }, 'Specifications & Details'),
                    product.details ? 
                        m('p', { class: 'text-lg opacity-80 leading-relaxed whitespace-pre-wrap font-mono text-sm bg-[var(--color-surface)] p-6 rounded-2xl border border-[var(--color-border)]' }, product.details)
                        : m('p', { class: 'opacity-60 italic' }, 'No detailed specifications provided for this product.')
                ]),

                // Tab Content: Reviews
                activeTab === 'reviews' && m('div', { id: 'reviews', class: 'animate-fade-in-up flex flex-col lg:flex-row gap-16' }, [
                    
                    // Review Form (Left)
                    m('div', { class: 'w-full lg:w-1/3 flex flex-col gap-6' }, [
                        
                        m('div', { class: 'flex items-center gap-4' }, [
                            m('div', { class: 'text-5xl font-black text-[var(--color-primary)]' }, reviewMeta?.average || '0.0'),
                            m('div', { class: 'flex flex-col' }, [
                                m('div', { class: 'flex text-yellow-500 text-lg gap-0.5' }, 
                                    Array.from({ length: 5 }).map((_, i) => m('iconify-icon', { icon: i < Math.round(reviewMeta?.average || 0) ? 'lucide:star' : 'lucide:star' }))
                                ),
                                m('span', { class: 'text-sm font-medium opacity-60 mt-0.5' }, `Based on ${reviewMeta?.count || 0} reviews`)
                            ])
                        ]),

                        m('hr', { class: 'border-[var(--color-border)] opacity-50 my-2' }),

                        Store.state.user ? m('div', { class: 'bg-[var(--color-surface)] border border-[var(--color-border)] p-6 rounded-2xl shadow-sm' }, [
                            m('h3', { class: 'text-base font-black mb-4' }, 'Write a Review'),
                            
                            // 5-Star Interactive Selector
                            m('div', { class: 'flex items-center gap-1 mb-4' }, 
                                Array.from({ length: 5 }).map((_, i) => 
                                    m('button', {
                                        class: `!m-0 !p-1 !bg-transparent !border-0 text-3xl transition-transform hover-spring cursor-pointer ${i < newRating ? 'text-yellow-500' : 'text-[var(--color-border)] hover:text-yellow-300'}`,
                                        onclick: () => vnode.state.newRating = i + 1
                                    }, m('iconify-icon', { icon: 'lucide:star' }))
                                )
                            ),

                            m('textarea', {
                                class: 'w-full p-4 rounded-xl border-2 border-[var(--color-border)] bg-[var(--color-bg-base)] text-[var(--color-text-base)] outline-none focus:border-[var(--color-primary)] transition-colors mb-4 resize-none',
                                rows: '4',
                                placeholder: 'Share your experience with this product...',
                                value: newComment,
                                oninput: (e: any) => vnode.state.newComment = e.target.value
                            }),

                            m(Button, {
                                variant: 'primary',
                                class: 'w-full',
                                loading: isSubmittingReview,
                                onclick: async () => {
                                    vnode.state.isSubmittingReview = true;
                                    try {
                                        await ApiService.addReview(product.id, newRating, newComment);
                                        Store.addToast('Review submitted successfully!', 'success');
                                        vnode.state.newComment = '';
                                        vnode.state.newRating = 5;
                                        // Refresh reviews
                                        const res = await ApiService.getReviews(product.id);
                                        if (res.success) {
                                            vnode.state.reviews = res.data;
                                            vnode.state.reviewMeta = res.meta;
                                        }
                                    } catch (e: any) {
                                        Store.addToast(e.message || 'Failed to submit review', 'error');
                                    } finally {
                                        vnode.state.isSubmittingReview = false;
                                        m.redraw();
                                    }
                                }
                            }, 'Submit Review')
                        ]) : m('div', { class: 'bg-[var(--color-surface)] border border-[var(--color-border)] p-6 rounded-2xl flex flex-col items-center justify-center text-center gap-3' }, [
                            m('p', { class: 'font-bold' }, 'Log in to rate this product'),
                            m(Button, { variant: 'outline', onclick: () => { Store.state.isAuthModalOpen = true; m.redraw(); } }, 'Sign In')
                        ])
                    ]),

                    // Testimonials List (Right)
                    m('div', { class: 'flex-1 flex flex-col gap-4' }, [
                        reviews.length === 0 
                            ? m('div', { class: 'py-12 border-2 border-dashed border-[var(--color-border)] rounded-2xl flex flex-col items-center justify-center text-center opacity-60' }, [
                                m('iconify-icon', { icon: 'lucide:message-square-dashed', class: 'text-4xl mb-3' }),
                                m('p', { class: 'font-bold' }, 'No reviews yet.'),
                                m('p', { class: 'text-sm' }, 'Be the first to share your thoughts!')
                            ])
                            : reviews.map((rev: any) => m('div', { 
                                key: rev.id, 
                                class: 'group p-5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl shadow-sm hover:shadow-md transition-shadow'
                            }, [
                                m('div', { class: 'flex items-center justify-between mb-3' }, [
                                    m('div', { class: 'flex items-center gap-3' }, [
                                        m('div', { class: 'w-10 h-10 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] border border-[var(--color-primary)]/20 flex items-center justify-center font-black uppercase text-sm' }, rev.user?.name?.[0] || 'U'),
                                        m('div', [
                                            m('p', { class: 'font-bold text-sm leading-none flex items-center gap-1.5' }, [
                                                rev.user?.name || 'Anonymous User',
                                                m('iconify-icon', { icon: 'lucide:badge-check', class: 'text-blue-500 text-xs' })
                                            ]),
                                            m('p', { class: 'text-[10px] opacity-50 uppercase tracking-widest mt-1 font-bold' }, new Date(rev.created_at).toLocaleDateString())
                                        ])
                                    ]),
                                    m('div', { class: 'flex text-yellow-500 text-sm' }, 
                                        Array.from({ length: 5 }).map((_, i) => m('iconify-icon', { icon: i < rev.rating ? 'lucide:star' : 'lucide:star' }))
                                    )
                                ]),
                                rev.comment && m('p', { class: 'text-sm opacity-80 leading-relaxed font-medium' }, rev.comment)
                            ]))
                    ])
                ])
            ]),

            /* ── RELATED PRODUCTS ────────────────────────────────────────────── */
            relatedProducts.length > 0 && m('div', { class: 'mt-24 pt-16 border-t border-[var(--color-border)]' }, [
                m('div', { class: 'flex items-center justify-between mb-8' }, [
                    m('h2', { class: 'text-2xl font-black tracking-tight' }, 'You May Also Like'),
                ]),
                m('div', { class: 'grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6' }, 
                    relatedProducts.map((p: any) => m(ProductCard, { key: p.id, product: p }))
                )
            ])
        ]);
    }
};
